import { FileText, Gavel, Globe, Landmark, Mail, Phone } from "lucide-react";
import type { DocumentsResponse } from "~/components/company/tabs/documents-tab";
import { formatCurrency, formatDate } from "~/lib/format";
import type { CompanyTenderStats } from "~/lib/tenders";
import type { CompanyProfile } from "~/types/company";
import { CardFooterLink, OverviewCard } from "./card";

/** ⑧ Dokumenty — 3 newest filings from the Sbírka listin. */
export function DocumentsTeaserCard({ documents }: { documents: DocumentsResponse | null }) {
	if (!documents || documents.documents.length === 0) return null;

	const newest = documents.documents.slice(0, 3);

	return (
		<OverviewCard icon={FileText} title="Dokumenty">
			<ul className="flex flex-col gap-2.5">
				{newest.map((doc) => (
					<li key={doc.id} className="flex items-baseline justify-between gap-3 text-sm">
						<span className="min-w-0 flex-1 truncate font-medium">
							{doc.title}
							{doc.year != null && <span className="text-muted-foreground"> · {doc.year}</span>}
						</span>
						{doc.filingDate && (
							<span className="shrink-0 font-mono text-xs text-muted-foreground tabular-nums">
								{formatDate(doc.filingDate)}
							</span>
						)}
					</li>
				))}
			</ul>
			<CardFooterLink to="dokumenty">Sbírka listin ({documents.total})</CardFooterLink>
		</OverviewCard>
	);
}

interface ContractsTeaserData {
	total: number;
	totalValueCzk: number | null;
}

/** ⑨ Smlouvy — renders only when the company has contracts in the registry. */
export function ContractsTeaserCard({ contracts }: { contracts: ContractsTeaserData | null }) {
	if (!contracts || contracts.total === 0) return null;

	return (
		<OverviewCard icon={Landmark} title="Smlouvy" hue="violet">
			<div className="flex flex-wrap gap-6 text-sm">
				<div>
					<p className="text-xs font-medium text-muted-foreground">Počet smluv</p>
					<p className="text-xl font-mono font-semibold tabular-nums">{contracts.total}</p>
				</div>
				{contracts.totalValueCzk != null && (
					<div>
						<p className="text-xs font-medium text-muted-foreground">Celková hodnota</p>
						<p className="text-xl font-mono font-semibold tabular-nums">
							{formatCurrency(contracts.totalValueCzk)}
						</p>
					</div>
				)}
			</div>
			<CardFooterLink to="smlouvy">Smlouvy v registru</CardFooterLink>
		</OverviewCard>
	);
}

/** Veřejné zakázky — separate from Registr smluv and based on procurement roles. */
export function TendersTeaserCard({ stats }: { stats: CompanyTenderStats | null }) {
	if (!stats) return null;
	const total = stats.asSupplier.participations + stats.asBuyer.tenders;
	if (total === 0) return null;

	return (
		<OverviewCard icon={Gavel} title="Veřejné zakázky" hue="amber">
			<div className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-3">
				<div>
					<p className="text-xs font-medium text-muted-foreground">Účasti</p>
					<p className="font-mono text-xl font-semibold tabular-nums">
						{stats.asSupplier.participations}
					</p>
				</div>
				<div>
					<p className="text-xs font-medium text-muted-foreground">Výhry</p>
					<p className="font-mono text-xl font-semibold tabular-nums">{stats.asSupplier.wins}</p>
				</div>
				<div>
					<p className="text-xs font-medium text-muted-foreground">Jako zadavatel</p>
					<p className="font-mono text-xl font-semibold tabular-nums">{stats.asBuyer.tenders}</p>
				</div>
			</div>
			<CardFooterLink to="zakazky">Zakázky, ceny a konkurenti</CardFooterLink>
		</OverviewCard>
	);
}

/** ⑩ Kontakty — web/e-mail/telefon; hidden until at least one source lands. */
export function ContactsCard({ company }: { company: CompanyProfile }) {
	const { website, email, phone } = company;
	if (!website && !email && !phone) return null;

	const websiteHref = website
		? website.startsWith("http")
			? website
			: `https://${website}`
		: null;

	return (
		<OverviewCard icon={Globe} title="Kontakty" hue="green">
			<ul className="flex flex-col gap-2.5 text-sm sm:flex-row sm:flex-wrap sm:gap-x-8">
				{website && websiteHref && (
					<li className="flex items-center gap-2">
						<Globe className="h-4 w-4 shrink-0 text-muted-foreground" />
						<a
							href={websiteHref}
							target="_blank"
							rel="noopener noreferrer nofollow"
							className="text-primary hover:underline break-all"
						>
							{website}
						</a>
					</li>
				)}
				{email && (
					<li className="flex items-center gap-2">
						<Mail className="h-4 w-4 shrink-0 text-muted-foreground" />
						<a href={`mailto:${email}`} className="text-primary hover:underline break-all">
							{email}
						</a>
					</li>
				)}
				{phone && (
					<li className="flex items-center gap-2">
						<Phone className="h-4 w-4 shrink-0 text-muted-foreground" />
						<a href={`tel:${phone}`} className="text-primary hover:underline">
							{phone}
						</a>
					</li>
				)}
			</ul>
		</OverviewCard>
	);
}
